import { useState, useRef, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { User, LogOut, Scale, Monitor, Settings, ExternalLink } from 'lucide-react';
import { ModernThemeToggle } from './ModernThemeToggle';
import { PLATFORM_URL } from '../../config/platformUrls';
import { AuthContext } from '../auth/AuthProvider';
import { createCrossOriginAuthUrl } from '../../utils/authSync';

interface User {
  email: string;
}

interface ProfileDropdownProps {
  user: User;
  onLogout: () => void;
}

export function ProfileDropdown({ user, onLogout }: ProfileDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { session } = useContext(AuthContext);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    setIsOpen(false);
    onLogout();
  };

  const handlePlatformLink = () => {
    const authUrl = createCrossOriginAuthUrl(PLATFORM_URL, session);
    window.open(authUrl, '_blank');
  };

  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center justify-center w-8 h-8 rounded-full 
          transition-all duration-200 ease-out
          focus:outline-none focus:ring-0
          ${
            isOpen
              ? 'bg-[#E34290] text-white shadow-md'
              : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600'
          }
        `}
        data-ph-capture-attribute="btn-profile-menu"
      >
        <User className="h-4 w-4" />
      </button>

      {/* Backdrop with fade animation */}
      {isOpen && <div className="fixed inset-0 z-40 lg:hidden" onClick={() => setIsOpen(false)} />}

      {/* Dropdown with slide and fade animation */}
      <div
        className={`
          absolute top-full -right-1 mt-3 w-72 
          bg-white dark:bg-gray-800 
          rounded-xl border border-gray-200 dark:border-gray-600
          z-50 overflow-hidden
          transition-all duration-200 ease-out origin-top-right
          ${
            isOpen
              ? 'opacity-100 scale-100 translate-y-0'
              : 'opacity-0 scale-95 translate-y-1 pointer-events-none'
          }
        `}
      >
        {/* Platform links */}
        <div className="p-4 border-b border-gray-100 dark:border-gray-600">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-gray-100 dark:bg-gray-700 rounded-lg">
              <Monitor className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">Platform</span>
          </div>
          <div className="grid grid-cols-1 gap-2">
            <button
              onClick={handlePlatformLink}
              className="flex items-center justify-between px-3 py-2 text-xs font-medium rounded-lg transition-all duration-150 bg-transparent text-gray-600 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-[#E34290] dark:hover:text-[#E34290]"
            >
              <span>Developer Platform</span>
              <ExternalLink className="h-3 w-3 ml-1" />
            </button>
          </div>
        </div>

        {/* User info */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-600">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-8 h-8 bg-gradient-to-br from-[#E34290] to-purple-500 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {user.email.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                {user.email}
              </p>
            </div>
          </div>
          <ModernThemeToggle />
        </div>

        {/* Menu items */}
        <div className="py-2">
          <Link
            to="/account"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
            data-ph-capture-attribute="nav-account-profile"
          >
            <Settings className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            Account Settings
          </Link>

          <Link
            to="/legal"
            onClick={() => setIsOpen(false)}
            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150"
            data-ph-capture-attribute="nav-legal-profile"
          >
            <Scale className="h-4 w-4 text-gray-500 dark:text-gray-400" />
            Legal Terms
          </Link>

          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors duration-150"
            data-ph-capture-attribute="btn-logout-profile"
          >
            <LogOut className="h-4 w-4" />
            Logout
          </button>
        </div>
      </div>
    </div>
  );
}
